import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { 
  getLibrarySections, 
  createLibrarySection,
  updateLibrarySection,
  deleteLibrarySection,
  getLibraryAdminOverview,
  getAdminLibraryCalls,
  getEligibleCallsForLibrary,
  addCallToLibrary,
  toggleLibraryCallVisibility,
  toggleLibraryCallFeatured,
  removeCallFromLibrary,
  getCallAudioUrl
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Library,
  Plus,
  Star,
  Eye,
  EyeOff,
  Trash2,
  MoreHorizontal,
  Search,
  BarChart3,
  Folder,
  Phone,
  Filter,
  Users,
  RefreshCw,
  Heart,
  Shield,
  Target,
  MessageSquare,
  Award,
  Headphones,
  TrendingUp,
  Clock,
  Edit,
  Check,
  X,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ICON_MAP = {
  Phone: Phone,
  Filter: Filter,
  Users: Users,
  RefreshCw: RefreshCw,
  Heart: Heart,
  Shield: Shield,
  Target: Target,
  MessageSquare: MessageSquare,
  Folder: Folder,
};

export default function AdminLibrary() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [sections, setSections] = useState([]);
  const [libraryCalls, setLibraryCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [eligibleCalls, setEligibleCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'visible', 'hidden', 'featured'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  
  // Audio playback
  const [playingCallId, setPlayingCallId] = useState(null);
  const [audioRef] = useState(() => new Audio());
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  
  // Add call dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCallToAdd, setSelectedCallToAdd] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  
  // Add section dialog
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  
  // View call dialog
  const [viewingCall, setViewingCall] = useState(null);

  const fetchData = async (showRefreshToast = false) => {
    if (showRefreshToast) setIsRefreshing(true);
    try {
      const [overviewRes, sectionsRes, callsRes, eligibleRes] = await Promise.all([
        getLibraryAdminOverview(),
        getLibrarySections(),
        getAdminLibraryCalls(true),
        getEligibleCallsForLibrary()
      ]);
      setOverview(overviewRes.data);
      setSections(sectionsRes.data);
      setLibraryCalls(callsRes.data);
      setFilteredCalls(callsRes.data);
      setEligibleCalls(eligibleRes.data);
      if (showRefreshToast) toast.success('Library refreshed');
    } catch (error) {
      console.error('Failed to fetch library data:', error);
      if (showRefreshToast) toast.error('Failed to refresh');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Apply filters when calls or filter changes
  useEffect(() => {
    let filtered = [...libraryCalls];
    
    // Filter by status
    if (activeFilter === 'visible') {
      filtered = filtered.filter(c => !c.is_hidden);
    } else if (activeFilter === 'hidden') {
      filtered = filtered.filter(c => c.is_hidden);
    } else if (activeFilter === 'featured') {
      filtered = filtered.filter(c => c.is_featured);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.call?.candidate_name?.toLowerCase().includes(query) ||
        c.user_name?.toLowerCase().includes(query) ||
        c.call?.call_type?.toLowerCase().includes(query)
      );
    }
    
    setFilteredCalls(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [libraryCalls, activeFilter, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredCalls.length / ITEMS_PER_PAGE);
  const paginatedCalls = filteredCalls.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Audio playback handler
  const handlePlayAudio = async (libraryCall) => {
    if (!libraryCall.call?.id) {
      toast.error('No audio available');
      return;
    }
    
    if (playingCallId === libraryCall.id) {
      audioRef.pause();
      setPlayingCallId(null);
      return;
    }
    
    setIsLoadingAudio(true);
    try {
      const result = await getCallAudioUrl(libraryCall.call.id);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      audioRef.src = result.url;
      audioRef.play().catch(() => toast.error('Failed to play audio'));
      setPlayingCallId(libraryCall.id);
    } catch (error) {
      toast.error('Failed to load audio');
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Stop audio when component unmounts
  useEffect(() => {
    return () => {
      audioRef.pause();
    };
  }, [audioRef]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddToLibrary = async () => {
    if (!selectedCallToAdd) return;
    
    try {
      await addCallToLibrary(selectedCallToAdd.id, selectedSections, adminNotes, isFeatured);
      toast.success('Call added to library');
      setShowAddDialog(false);
      setSelectedCallToAdd(null);
      setSelectedSections([]);
      setAdminNotes('');
      setIsFeatured(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add call');
    }
  };

  const handleToggleVisibility = async (libraryCall) => {
    try {
      await toggleLibraryCallVisibility(libraryCall.id, !libraryCall.is_hidden);
      toast.success(libraryCall.is_hidden ? 'Call is now visible' : 'Call hidden');
      fetchData();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleToggleFeatured = async (libraryCall) => {
    try {
      await toggleLibraryCallFeatured(libraryCall.id, !libraryCall.is_featured);
      toast.success(libraryCall.is_featured ? 'Call unfeatured' : 'Call featured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleRemoveFromLibrary = async (libraryCallId) => {
    if (!confirm('Remove this call from the library?')) return;
    
    try {
      await removeCallFromLibrary(libraryCallId);
      toast.success('Call removed from library');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove call');
    }
  };

  const handleCreateSection = async () => {
    if (!newSectionName.trim()) return;
    
    try {
      await createLibrarySection(newSectionName, newSectionDescription, 'Folder');
      toast.success('Section created');
      setShowSectionDialog(false);
      setNewSectionName('');
      setNewSectionDescription('');
      fetchData();
    } catch (error) {
      toast.error('Failed to create section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!confirm('Delete this section? Calls will remain in library.')) return;
    
    try {
      await deleteLibrarySection(sectionId);
      toast.success('Section deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  const getSectionIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Folder;
    return <IconComponent className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-library">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Library className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading">Library Management</h1>
            <p className="text-amber-100">Curate the best calls for your team</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => fetchData(true)}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setShowSectionDialog(true)}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Folder className="h-4 w-4 mr-2" />
            New Section
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setShowAddDialog(true)}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Calls
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable */}
      {overview?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`card-elevated cursor-pointer transition-all hover:shadow-lg ${activeFilter === 'all' ? 'ring-2 ring-amber-500' : 'border-amber-200'}`}
            onClick={() => setActiveFilter('all')}
            data-testid="filter-all"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Library className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{overview.stats.total_calls}</p>
                  <p className="text-xs text-muted-foreground">Total Calls</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-elevated cursor-pointer transition-all hover:shadow-lg ${activeFilter === 'visible' ? 'ring-2 ring-emerald-500' : 'border-emerald-200'}`}
            onClick={() => setActiveFilter('visible')}
            data-testid="filter-visible"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Eye className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{overview.stats.visible_calls}</p>
                  <p className="text-xs text-muted-foreground">Visible</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-elevated cursor-pointer transition-all hover:shadow-lg ${activeFilter === 'hidden' ? 'ring-2 ring-slate-500' : 'border-slate-200'}`}
            onClick={() => setActiveFilter('hidden')}
            data-testid="filter-hidden"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <EyeOff className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{overview.stats.hidden_calls}</p>
                  <p className="text-xs text-muted-foreground">Hidden</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-elevated cursor-pointer transition-all hover:shadow-lg ${activeFilter === 'featured' ? 'ring-2 ring-yellow-500' : 'border-yellow-200'}`}
            onClick={() => setActiveFilter('featured')}
            data-testid="filter-featured"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{overview.stats.featured_calls}</p>
                  <p className="text-xs text-muted-foreground">Featured</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="calls" className="space-y-6">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="calls" className="gap-2">
            <Library className="h-4 w-4" />
            Library Calls
          </TabsTrigger>
          <TabsTrigger value="sections" className="gap-2">
            <Folder className="h-4 w-4" />
            Sections
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Library Calls Tab */}
        <TabsContent value="calls" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Library className="h-5 w-5 text-amber-500" />
                    {activeFilter === 'all' ? 'All Library Calls' : 
                     activeFilter === 'visible' ? 'Visible Calls' :
                     activeFilter === 'hidden' ? 'Hidden Calls' : 'Featured Calls'}
                    <Badge variant="outline">{filteredCalls.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    {activeFilter !== 'all' && (
                      <Button variant="link" className="p-0 h-auto text-xs" onClick={() => setActiveFilter('all')}>
                        ← Show all calls
                      </Button>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search calls..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-[200px]"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Play</TableHead>
                    <TableHead>Call</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Sections</TableHead>
                    <TableHead className="text-center">Views</TableHead>
                    <TableHead className="text-center">Listens</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCalls.map((lc) => (
                    <TableRow 
                      key={lc.id} 
                      className={`${lc.is_hidden ? 'opacity-50 bg-muted/50' : ''} hover:bg-amber-50/50 cursor-pointer`}
                      onClick={() => setViewingCall(lc)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handlePlayAudio(lc)}
                                disabled={isLoadingAudio}
                              >
                                {playingCallId === lc.id ? (
                                  <Pause className="h-4 w-4 text-amber-600" />
                                ) : isLoadingAudio ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4 text-emerald-600" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {playingCallId === lc.id ? 'Pause' : 'Play Audio'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {lc.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                          <div>
                            <p className="font-medium">{lc.call?.candidate_name || 'Call'}</p>
                            <p className="text-xs text-muted-foreground capitalize">{lc.call?.call_type}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{lc.user_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={
                          lc.call?.average_score >= 9 ? 'bg-emerald-100 text-emerald-700' :
                          lc.call?.average_score >= 7 ? 'bg-blue-100 text-blue-700' :
                          'bg-muted text-slate-700'
                        }>
                          {lc.call?.average_score?.toFixed(1) || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {lc.section_ids?.slice(0, 2).map((sid) => {
                            const section = sections.find(s => s.id === sid);
                            return section ? (
                              <Badge key={sid} variant="outline" className="text-xs">
                                {section.name.substring(0, 15)}
                              </Badge>
                            ) : null;
                          })}
                          {lc.section_ids?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{lc.section_ids.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{lc.view_count || 0}</TableCell>
                      <TableCell className="text-center">{lc.listen_count || 0}</TableCell>
                      <TableCell className="text-center">
                        {lc.is_hidden ? (
                          <Badge variant="outline" className="text-muted-foreground">Hidden</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700">Visible</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setViewingCall(lc)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => lc.call?.id && navigate(`${adminUrl || '/dashboard'}/call/${lc.call.id}`)}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open Full Page
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleFeatured(lc)}>
                              <Star className="h-4 w-4 mr-2" />
                              {lc.is_featured ? 'Unfeature' : 'Feature'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleVisibility(lc)}>
                              {lc.is_hidden ? (
                                <><Eye className="h-4 w-4 mr-2" /> Show to Users</>
                              ) : (
                                <><EyeOff className="h-4 w-4 mr-2" /> Hide from Users</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleRemoveFromLibrary(lc.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove from Library
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredCalls.length)} of {filteredCalls.length} calls
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              
              {filteredCalls.length === 0 && (
                <div className="text-center py-12">
                  <Library className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery ? 'No calls match your search' : 
                     activeFilter !== 'all' ? `No ${activeFilter} calls` : 'No calls in library yet'}
                  </p>
                  {activeFilter === 'all' && !searchQuery && (
                    <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Calls
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sections Tab */}
        <TabsContent value="sections" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Folder className="h-5 w-5 text-indigo-500" />
                  Library Sections
                </CardTitle>
                <CardDescription>Organize calls into categories</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowSectionDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Section
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {overview?.sections?.map((section) => (
                  <Card key={section.id} className="border hover:border-indigo-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            {getSectionIcon(section.icon)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{section.name}</h4>
                            <Badge variant="outline" className="text-xs mt-1">
                              {section.section_type}
                            </Badge>
                          </div>
                        </div>
                        {section.section_type === 'custom' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteSection(section.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {section.description && (
                        <p className="text-sm text-muted-foreground mb-2">{section.description}</p>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Calls in section</span>
                        <span className="font-semibold text-indigo-600">{section.call_count}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Popular Searches */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-500" />
                  Popular Searches
                </CardTitle>
              </CardHeader>
              <CardContent>
                {overview?.popular_searches?.length > 0 ? (
                  <div className="space-y-3">
                    {overview.popular_searches.map((search, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <span className="text-sm text-slate-700">&quot;{search.query}&quot;</span>
                        <Badge variant="outline">{search.count} searches</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No searches yet</p>
                )}
              </CardContent>
            </Card>

            {/* Most Popular Calls */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  Most Popular Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                {overview?.popular_calls?.length > 0 ? (
                  <div className="space-y-3">
                    {overview.popular_calls.map((call, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-amber-500" />
                          <span className="text-sm text-slate-700">
                            {call.call_details?.candidate_name || 'Call'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" /> {call.view_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Headphones className="h-3 w-3" /> {call.listen_count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-500" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.recent_activity?.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {overview.recent_activity.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2">
                        {log.action === 'listen' ? (
                          <Headphones className="h-4 w-4 text-emerald-500" />
                        ) : log.action === 'view' ? (
                          <Eye className="h-4 w-4 text-blue-500" />
                        ) : (
                          <Search className="h-4 w-4 text-purple-500" />
                        )}
                        <span className="text-sm text-slate-700">
                          {log.user_name || 'User'} {log.action === 'search' ? `searched "${log.search_query}"` : `${log.action}ed a call`}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {log.created_at ? format(new Date(log.created_at), 'MMM d, HH:mm') : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Call Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-amber-500" />
              Add Calls to Library
            </DialogTitle>
            <DialogDescription>
              Select calls to add to the library (sorted by score, highest first)
            </DialogDescription>
          </DialogHeader>
          
          {selectedCallToAdd ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">{selectedCallToAdd.candidate_name}</h4>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {selectedCallToAdd.average_score?.toFixed(1)}/10
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">by {selectedCallToAdd.user_name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Sections</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {sections.map((section) => (
                    <label key={section.id} className="flex items-center gap-2 p-2 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <Checkbox
                        checked={selectedSections.includes(section.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedSections([...selectedSections, section.id]);
                          } else {
                            setSelectedSections(selectedSections.filter(id => id !== section.id));
                          }
                        }}
                      />
                      <span className="text-sm">{section.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Admin Notes (optional)</label>
                <Input
                  placeholder="Why is this call being added to the library?"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={isFeatured}
                  onCheckedChange={setIsFeatured}
                />
                <span className="text-sm">Feature this call (show at top)</span>
              </label>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedCallToAdd(null)}>
                  Back
                </Button>
                <Button onClick={handleAddToLibrary}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Library
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {eligibleCalls.length > 0 ? eligibleCalls.map((call) => (
                <div 
                  key={call.id} 
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-amber-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedCallToAdd(call)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Award className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{call.candidate_name || 'Call'}</p>
                      <p className="text-sm text-muted-foreground">by {call.user_name} • {call.call_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      call.classification === 'Best' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                    }>
                      {call.classification}
                    </Badge>
                    <Badge variant="outline">{call.average_score?.toFixed(1)}/10</Badge>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <Library className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <p className="text-muted-foreground">No eligible calls found</p>
                  <p className="text-sm text-muted-foreground">All analyzed calls can be added</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Section Dialog */}
      <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-indigo-500" />
              Create New Section
            </DialogTitle>
            <DialogDescription>
              Create a custom section to organize calls
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Section Name *</label>
              <Input
                placeholder="e.g., 'Best Negotiations'"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Description (optional)</label>
              <Input
                placeholder="Describe what calls belong in this section"
                value={newSectionDescription}
                onChange={(e) => setNewSectionDescription(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSectionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSection} disabled={!newSectionName.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Call Details Dialog */}
      <Dialog open={!!viewingCall} onOpenChange={() => setViewingCall(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="h-5 w-5 text-amber-500" />
              Call Details
            </DialogTitle>
          </DialogHeader>
          
          {viewingCall && (
            <div className="space-y-4">
              {/* Call Info Header */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {viewingCall.is_featured && <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />}
                      <h3 className="font-semibold text-lg">{viewingCall.call?.candidate_name || 'Call'}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">by {viewingCall.user_name}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-1">{viewingCall.call?.call_type} • {viewingCall.call?.call_date ? format(new Date(viewingCall.call.call_date), 'MMM d, yyyy') : '-'}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={`text-lg px-3 py-1 ${
                      viewingCall.call?.average_score >= 9 ? 'bg-emerald-100 text-emerald-700' :
                      viewingCall.call?.average_score >= 7 ? 'bg-blue-100 text-blue-700' :
                      'bg-muted text-slate-700'
                    }`}>
                      {viewingCall.call?.average_score?.toFixed(1) || '-'}/10
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Audio Player */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant={playingCallId === viewingCall.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePlayAudio(viewingCall)}
                      disabled={isLoadingAudio}
                    >
                      {playingCallId === viewingCall.id ? (
                        <><Pause className="h-4 w-4 mr-2" /> Pause</>
                      ) : isLoadingAudio ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                      ) : (
                        <><Play className="h-4 w-4 mr-2" /> Play Audio</>
                      )}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Duration: {viewingCall.call?.duration ? `${Math.floor(viewingCall.call.duration / 60)}:${(viewingCall.call.duration % 60).toString().padStart(2, '0')}` : '-'}
                    </span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => viewingCall.call?.id && navigate(`${adminUrl || '/dashboard'}/call/${viewingCall.call.id}`)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Full Details
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <Eye className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-700">{viewingCall.view_count || 0}</p>
                  <p className="text-xs text-blue-600">Views</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <Headphones className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-emerald-700">{viewingCall.listen_count || 0}</p>
                  <p className="text-xs text-emerald-600">Listens</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <Folder className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-purple-700">{viewingCall.section_ids?.length || 0}</p>
                  <p className="text-xs text-purple-600">Sections</p>
                </div>
              </div>

              {/* Sections */}
              {viewingCall.section_ids?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">In Sections</h4>
                  <div className="flex flex-wrap gap-2">
                    {viewingCall.section_ids.map((sid) => {
                      const section = sections.find(s => s.id === sid);
                      return section ? (
                        <Badge key={sid} variant="outline">
                          {getSectionIcon(section.icon)}
                          <span className="ml-1">{section.name}</span>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              {viewingCall.admin_notes && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Admin Notes</h4>
                  <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">{viewingCall.admin_notes}</p>
                </div>
              )}

              {/* Status and Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Badge variant={viewingCall.is_hidden ? "outline" : "default"} className={!viewingCall.is_hidden ? "bg-emerald-600" : ""}>
                    {viewingCall.is_hidden ? 'Hidden from Users' : 'Visible to Users'}
                  </Badge>
                  {viewingCall.is_featured && (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <Star className="h-3 w-3 mr-1 fill-yellow-500" /> Featured
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      handleToggleVisibility(viewingCall);
                      setViewingCall(null);
                    }}
                  >
                    {viewingCall.is_hidden ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                    {viewingCall.is_hidden ? 'Show' : 'Hide'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      handleToggleFeatured(viewingCall);
                      setViewingCall(null);
                    }}
                  >
                    <Star className="h-4 w-4 mr-1" />
                    {viewingCall.is_featured ? 'Unfeature' : 'Feature'}
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => {
                      handleRemoveFromLibrary(viewingCall.id);
                      setViewingCall(null);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
